import type { PatientCoreFieldDef, PatientFormStep } from '@helios/domain';

/**
 * Pure (PowerSync-free) patient form-settings types + merge logic. Kept
 * import-light so it is unit testable under the repo's node:test harness.
 */

/**
 * Row shape returned from patient_field_definitions PowerSync table (override rows).
 * All override columns are nullable — only non-null values represent an override.
 */
export interface PatientFieldDefinitionRow {
  id: string;
  key: string;
  field_type: string;
  category: string;
  default_label: string | null;
  default_enabled: number | null; // 0 | 1 | null
  required_override: number | null; // 0 | 1 | null
  order_override: number | null;
  width_override: string | null; // 'half' | 'full' | null
  validation_override: string | null; // JSON string or null
  updated_at: string | null;
}

/** View model for a single patient form field as displayed in settings UI. */
export type EffectivePatientField = {
  field_key: string;
  label: string;
  step: PatientFormStep;
  visible: boolean;
  required: boolean;
  order: number;
  width: 'half' | 'full';
  field_type?: string;
  locked?: boolean;
  notes?: string;
  /** Raw JSON string from patient_field_definitions.validation_override */
  validation_override: string | null;
  /** Tracks which properties have been overridden from code defaults */
  overrides: {
    label?: boolean;
    visible?: boolean;
    required?: boolean;
    order?: boolean;
    width?: boolean;
  };
};

/**
 * Pure merge: (code defaults) + (DB overrides) → effective settings VM.
 *
 * - Always returns one row per core field (never empty if coreFields is populated).
 * - DB rows only override non-null properties; code defaults fill the rest.
 * - The `overrides` map tracks which properties came from DB for UI hints.
 */
export function buildPatientFormSettingsVM(
  coreFields: readonly PatientCoreFieldDef[],
  dbOverrides: PatientFieldDefinitionRow[],
): EffectivePatientField[] {
  const overrideMap = new Map<string, PatientFieldDefinitionRow>();
  for (const row of dbOverrides) {
    overrideMap.set(row.key, row);
  }

  return coreFields.map((def) => {
    const dbRow = overrideMap.get(def.field_key);
    const overrides: EffectivePatientField['overrides'] = {};

    let label = def.label;
    let visible = def.visible;
    let required = def.required;
    let order = def.order;
    let width: 'half' | 'full' = 'half';
    let validation_override: string | null = null;

    if (dbRow) {
      if (dbRow.default_label != null) {
        label = dbRow.default_label;
        overrides.label = true;
      }
      if (dbRow.default_enabled != null) {
        visible = dbRow.default_enabled === 1;
        overrides.visible = true;
      }
      if (dbRow.required_override != null) {
        required = dbRow.required_override === 1;
        overrides.required = true;
      }
      if (dbRow.order_override != null) {
        order = dbRow.order_override;
        overrides.order = true;
      }
      if (dbRow.width_override === 'half' || dbRow.width_override === 'full') {
        width = dbRow.width_override;
        overrides.width = true;
      }
      if (dbRow.validation_override != null) {
        validation_override = dbRow.validation_override;
      }
    }

    return {
      field_key: def.field_key,
      label,
      step: def.step,
      visible,
      required,
      order,
      width,
      field_type: def.type,
      locked: def.locked,
      notes: def.notes,
      validation_override,
      overrides,
    };
  });
}
